import React, { useEffect, useRef, useState } from 'react';
import { FULL_NAME } from '../../config/constants';
import portrait1 from '../../img/backgrounds/portrait_bw_dark.jpg';
import portrait2 from '../../img/backgrounds/portrait_bw_light.jpg';
import portrait3 from '../../img/backgrounds/portrait_yosemite_1.jpg';
import portrait4 from '../../img/backgrounds/portrait_yosemite_2.jpg';

const portraits = [portrait1, portrait2, portrait3, portrait4];

const getRandomPortrait = (currentPortrait?: string) => {
  let availablePortraits = portraits;

  // If there's a current portrait, exclude it to ensure we get a different one
  if (currentPortrait) {
    availablePortraits = portraits.filter(p => p !== currentPortrait);
  }

  // If we filtered out all portraits (shouldn't happen), use all portraits
  if (availablePortraits.length === 0) {
    availablePortraits = portraits;
  }

  const randomIndex = Math.floor(Math.random() * availablePortraits.length);
  return availablePortraits[randomIndex];
};

export const HeroPortrait = () => {
  const [currentPortrait, setCurrentPortrait] = useState(() => getRandomPortrait());
  const [nextPortrait, setNextPortrait] = useState<string | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [nextImageLoaded, setNextImageLoaded] = useState(false);
  const [isCompletingTransition, setIsCompletingTransition] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const loadedImagesRef = useRef<Set<string>>(new Set());
  // Use refs to track state for interval checks to avoid stale closures
  const isTransitioningRef = useRef(false);
  const nextPortraitRef = useRef<string | null>(null);
  const completionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isCompletingRef = useRef(false);
  const skipTransitionRef = useRef(false);

  // Sync refs with state changes
  useEffect(() => {
    isTransitioningRef.current = isTransitioning;
  }, [isTransitioning]);

  useEffect(() => {
    nextPortraitRef.current = nextPortrait;
  }, [nextPortrait]);

  // Clear skipTransition after render to allow normal transitions on next change
  useEffect(() => {
    if (skipTransitionRef.current) {
      // Use requestAnimationFrame to clear after render is complete
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          skipTransitionRef.current = false;
        });
      });
    }
  }, [currentPortrait]);

  // Preload all images on mount
  useEffect(() => {
    const preloadAllImages = () => {
      portraits.forEach((portrait) => {
        if (loadedImagesRef.current.has(portrait)) return;
        const img = new Image();
        img.onload = () => loadedImagesRef.current.add(portrait);
        img.onerror = () => loadedImagesRef.current.add(portrait); // still mark as handled
        img.src = portrait;
      });
    };

    preloadAllImages();
  }, []);

  // Preload next image when it's set
  useEffect(() => {
    if (!nextPortrait) {
      return;
    }

    console.log('[HeroPortrait] Next portrait queued:', nextPortrait);

    // Reset loaded flag when we queue a new portrait
    setNextImageLoaded(false);

    if (loadedImagesRef.current.has(nextPortrait)) {

      // Set synchronously - use requestAnimationFrame to ensure it happens after current render cycle
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setNextImageLoaded(true);
        });
      });
      return;
    }


    const img = new Image();
    img.onload = () => {
      loadedImagesRef.current.add(nextPortrait);

      setNextImageLoaded(true);
    };
    img.onerror = () => {
      // Still proceed; mark as handled
      loadedImagesRef.current.add(nextPortrait);

      setNextImageLoaded(true);
    };
    img.src = nextPortrait;
  }, [nextPortrait]);

  // Start cross-fade transition when next image is loaded
  useEffect(() => {
    // Only start transition if we have all conditions and aren't already transitioning or completing
    if (nextImageLoaded && nextPortrait && !isTransitioning && !isCompletingRef.current) {


      // Clear any existing completion timeout (shouldn't happen, but be safe)
      if (completionTimeoutRef.current) {
        console.log('[HeroPortrait] WARNING: Clearing existing completion timeout');
        clearTimeout(completionTimeoutRef.current);
      }

      // Ensure next image is in the DOM before starting fade
      const startTransition = setTimeout(() => {

        setIsTransitioning(true);
      }, 100);

      // After fade completes, update current portrait
      // Store the portrait value in a ref so it persists even if nextPortrait changes
      const portraitToSet = nextPortrait;
      isCompletingRef.current = true; // Mark that we're completing to prevent new transitions
      completionTimeoutRef.current = setTimeout(() => {

        console.log('[HeroPortrait] Setting current portrait to:', portraitToSet);

        // Set skipTransition before updating currentPortrait to prevent flash
        skipTransitionRef.current = true;

        // Mark that we're completing transition - this will adjust z-index
        setIsCompletingTransition(true);

        // Update current portrait first - this ensures the new image is in DOM
        setCurrentPortrait(portraitToSet);

        // Wait for React to process the update, then fade out and remove next image
        setTimeout(() => {
          // Clear transitioning state to remove fade-in class from next image
          // This will fade it out (opacity 1 -> 0)
          setIsTransitioning(false);

          // Wait for fade-out transition to complete, then remove next image
          setTimeout(() => {
            // Remove next image from DOM after it has faded out
            setNextPortrait(null);
            setNextImageLoaded(false);
            setIsCompletingTransition(false);

            // Clear flags after a brief delay to ensure everything is settled
            setTimeout(() => {
              completionTimeoutRef.current = null;
              isCompletingRef.current = false; // Clear completing flag
              // skipTransition will be cleared by useEffect when currentPortrait changes
              console.log('[HeroPortrait] Transition complete, state reset. Current:', portraitToSet);
            }, 10);
          }, 1000); // Wait for fade-out transition to complete (1s)
        }, 50); // Wait for React to update current portrait in DOM
      }, 1100); // 100ms delay + 1000ms transition



      return () => {
        // Only clear startTransition, NOT the completion timeout
        // The completion timeout must run to reset state
        clearTimeout(startTransition);
        console.log('[HeroPortrait] Cleanup: cleared startTransition timeout');
      };
    }
  }, [nextImageLoaded, nextPortrait, isTransitioning]);

  useEffect(() => {
    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    console.log('[HeroPortrait] Setting up interval, current portrait:', currentPortrait);

    // Change portrait every 20 seconds (20000 milliseconds) - reduced for testing
    intervalRef.current = setInterval(() => {
      // Use refs to check current state (avoid stale closures)
      if (isTransitioningRef.current || nextPortraitRef.current) {
        console.log('[HeroPortrait] Interval skipped - transition in progress or next portrait queued', {
          isTransitioning: isTransitioningRef.current,
          nextPortrait: nextPortraitRef.current
        });
        return;
      }

      const newPortrait = getRandomPortrait(currentPortrait);
      console.log('[HeroPortrait] Interval triggered, queuing new portrait:', newPortrait);
      setNextPortrait(newPortrait);
    }, 20000); // 20 seconds for testing

    // Cleanup interval on unmount or deps change
    return () => {
      if (intervalRef.current) {
        console.log('[HeroPortrait] Cleaning up interval');
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [currentPortrait]); // Only depend on currentPortrait to avoid recreating interval too often

  return (
    <div className="hero-image-wrapper">
      {/* Current image */}
      <img
        src={currentPortrait}
        alt={FULL_NAME}
        className={`hero-portrait hero-portrait-current ${isTransitioning ? 'fade-out' : ''} ${skipTransitionRef.current ? 'no-transition' : ''} ${isCompletingTransition ? 'completing' : ''}`}
      />
      {/* Next image - render when set and loaded, fade in when transitioning */}
      {nextPortrait && nextImageLoaded && (
        <img
          src={nextPortrait}
          alt={FULL_NAME}
          className={`hero-portrait hero-portrait-next ${isTransitioning ? 'fade-in' : ''} ${isCompletingTransition ? 'completing' : ''}`}
        />
      )}
    </div>
  );
};

